//
//  SwimDashboardPage.js
//
//
//  Created by Daniel Kanaan on 6/17/25.
//

// src/SwimDashboardPage.js

import React, { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import SwimListPage from './SwimListPage';
import SwimMapPage from './SwimMapPage';
import { useRecentSwims } from './RecentSwimsContext';

// Helper to parse the swim ID from the URL hash
const getSwimIdFromHash = () => window.location.hash.split('/')[1] || null;

const SwimDashboardPage = ({ onCurateArea }) => {
  const { swims, loading, error, loadRecentSwims } = useRecentSwims();
  
  // State for the UI
  const [view, setView] = useState('list'); // 'list' or 'map'
  const [selectedSwimId, setSelectedSwimId] = useState(getSwimIdFromHash);
  
  // State for the selected swim's data
  const [activeRoute, setActiveRoute] = useState(null);
  const [routeCache, setRouteCache] = useState(new Map()); // Our new route cache
  const [routeLoading, setRouteLoading] = useState(false);
  
  useEffect(() => {
    void loadRecentSwims();
  }, [loadRecentSwims]);
  
  // Effect to handle URL changes (back/forward/initial load)
  useEffect(() => {
    const handleHashChange = () => {
      setSelectedSwimId(getSwimIdFromHash());
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);
  
  // Effect to fetch the route when a swim is selected
  const fetchRouteForSwim = useCallback(async (docId) => {
    if (!docId) {
      setActiveRoute(null);
      return;
    }
    const swim = swims.find(s => s.id === docId);
    if (!swim || !swim.userID || !swim.swimID) {
      setActiveRoute(null); // Clear route if swim data is not available
      return;
    }

    // Use a composite key for the cache: `${userID}-${swimID}`
    const cacheKey = `${swim.userID}-${swim.swimID}`;

    if (routeCache.has(cacheKey)) {
      setActiveRoute(routeCache.get(cacheKey));
      return;
    }

    setRouteLoading(true);
    setActiveRoute(null); // Clear previous route while loading

    try {
      const getRouteForSwim = httpsCallable(functions, 'getRouteForSwim');
      // Pass both userId and the local swimID (which is the doc ID in the routes subcollection)
      const result = await getRouteForSwim({ userId: swim.userID, swimId: swim.swimID });
      const geojson = {
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: result.data.coordinates },
      };
      // Update cache and active route
      setRouteCache(prevCache => new Map(prevCache).set(cacheKey, geojson));
      setActiveRoute(geojson);
    } catch (error) {
      console.error(`Failed to fetch route for swim ${docId}:`, error);
      // Optionally set an error state for the route
    } finally {
      setRouteLoading(false);
    }
  }, [swims, routeCache]); // Now depends on the full swims list and cache
  
  useEffect(() => {
    fetchRouteForSwim(selectedSwimId);
  }, [selectedSwimId, fetchRouteForSwim]);
  
  // Handler for when a user selects a swim
  const handleSwimSelect = (swimId) => {
    // Update the URL hash, which will trigger the useEffect to update state
    window.location.hash = swimId ? `swims/${swimId}` : 'swims';
  };
  
  const selectedSwim = swims.find(s => s.id === selectedSwimId);
  
  return (
    <div className={`swim-dashboard ${view === 'map' ? 'is-map-view' : ''}`}>
    <div className="view-toggle">
    <button onClick={() => setView('list')} className={view === 'list' ? 'active' : ''}>List</button>
    <button onClick={() => setView('map')} className={view === 'map' ? 'active' : ''}>Map</button>
    </div>
    
    <div className="dashboard-content">
    {view === 'list' ? (
      <SwimListPage
       swims={swims}
       loading={loading}
       error={error}
       selectedSwimId={selectedSwimId}
       onSwimSelect={handleSwimSelect}
       activeRoute={activeRoute}
       routeLoading={routeLoading}
       onCurateArea={onCurateArea}
       onRefresh={() => loadRecentSwims({ refresh: true })}
       />
       ) : (
        <SwimMapPage
        swims={swims}
        selectedSwim={selectedSwim}
        activeRoute={activeRoute}
        onSwimSelect={handleSwimSelect}
        routeLoading={routeLoading}
        />
        )}
    </div>
    </div>
    );
};

export default SwimDashboardPage;
